import { ManagerAuthenticatedResult, ManagerCodeRequested, ManagerMeResponse } from '@odonix/contracts';

/**
 * Сессия кабинета менеджера: почта → код из письма → кабинет.
 *
 * Тот же разрез, что у платформенной админки: access-токен в памяти, refresh —
 * в HttpOnly-cookie `manager_refresh`, локальной базы нет. Отличие во входе:
 * пароля нет, есть код на почту, и между шагами помнится только адрес.
 */

export type SessionState =
  | { status: 'restoring' }
  | { status: 'email'; error: string | null; busy: boolean }
  | {
      status: 'code';
      email: string;
      error: string | null;
      notice: string | null;
      busy: boolean;
      /** Раньше этого момента новый код не запросить — сервер молча не пришлёт. */
      resendAt: number;
    }
  | { status: 'ready'; manager: ManagerMeResponse };

export interface SessionStore {
  snapshot: () => SessionState;
  subscribe: (listener: () => void) => () => void;
  restore: () => Promise<void>;
  requestCode: (email: string) => Promise<void>;
  resendCode: () => Promise<void>;
  verifyCode: (code: string) => Promise<void>;
  changeEmail: () => void;
  logout: () => Promise<void>;
  /** Единственная дверь к API для остальных модулей: заголовок и один повтор
   *  после обновления токена. */
  authorizedFetch: (path: string, init?: RequestInit) => Promise<Response>;
}

async function messageOf(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { message?: unknown; fields?: Record<string, unknown> };
    const field = body.fields === undefined ? undefined : Object.values(body.fields).find((value) => typeof value === 'string');
    if (typeof field === 'string') return field;
    return typeof body.message === 'string' ? body.message : fallback;
  } catch {
    return fallback;
  }
}

const OFFLINE = 'Нет связи с сервером. Проверьте интернет и повторите';

export function createSessionStore(options: { baseUrl: string }): SessionStore {
  const base = options.baseUrl.replace(/\/+$/, '');
  let state: SessionState = { status: 'restoring' };
  let accessToken: string | null = null;
  const listeners = new Set<() => void>();

  function setState(next: SessionState): void {
    state = next;
    for (const listener of listeners) listener();
  }

  async function refresh(): Promise<boolean> {
    const response = await fetch(`${base}/v1/manager/auth/refresh`, { method: 'POST', credentials: 'include' });
    if (!response.ok) return false;
    accessToken = ((await response.json()) as { accessToken: string }).accessToken;
    return true;
  }

  async function sendCode(email: string): Promise<{ ok: true; resendAt: number } | { ok: false; error: string }> {
    let response: Response;
    try {
      response = await fetch(`${base}/v1/manager/auth/code`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch {
      return { ok: false, error: OFFLINE };
    }
    if (!response.ok) return { ok: false, error: await messageOf(response, 'Код не отправлен. Повторите') };
    const body = ManagerCodeRequested.parse(await response.json());
    return { ok: true, resendAt: Date.now() + body.resendAfterSec * 1000 };
  }

  return {
    snapshot: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    async restore() {
      try {
        if (!(await refresh())) {
          setState({ status: 'email', error: null, busy: false });
          return;
        }
        const response = await fetch(`${base}/v1/manager/me`, {
          headers: { authorization: `Bearer ${accessToken ?? ''}` },
        });
        if (!response.ok) throw new Error('me');
        setState({ status: 'ready', manager: ManagerMeResponse.parse(await response.json()) });
      } catch {
        setState({ status: 'email', error: null, busy: false });
      }
    },

    async requestCode(email) {
      setState({ status: 'email', error: null, busy: true });
      const result = await sendCode(email);
      if (!result.ok) {
        setState({ status: 'email', error: result.error, busy: false });
        return;
      }
      setState({ status: 'code', email, error: null, notice: null, busy: false, resendAt: result.resendAt });
    },

    async resendCode() {
      if (state.status !== 'code') return;
      const { email } = state;
      setState({ ...state, busy: true, error: null, notice: null });
      const result = await sendCode(email);
      // Пока письмо уходило, человек мог нажать «Другая почта».
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (state.status !== 'code') return;
      setState(
        result.ok
          ? { ...state, busy: false, notice: 'Новый код отправлен — действует последний', resendAt: result.resendAt }
          : { ...state, busy: false, error: result.error },
      );
    },

    async verifyCode(code) {
      if (state.status !== 'code') return;
      const { email } = state;
      setState({ ...state, busy: true, error: null, notice: null });

      let response: Response;
      try {
        response = await fetch(`${base}/v1/manager/auth/verify`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email, code }),
        });
      } catch {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (state.status === 'code') setState({ ...state, busy: false, error: OFFLINE });
        return;
      }

      if (!response.ok) {
        const error = await messageOf(response, 'Код не подошёл');
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (state.status === 'code') setState({ ...state, busy: false, error });
        return;
      }

      const body = ManagerAuthenticatedResult.parse(await response.json());
      accessToken = body.accessToken;
      setState({ status: 'ready', manager: body.manager });
    },

    changeEmail() {
      setState({ status: 'email', error: null, busy: false });
    },

    async logout() {
      try {
        await fetch(`${base}/v1/manager/auth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: { authorization: `Bearer ${accessToken ?? ''}` },
        });
      } finally {
        accessToken = null;
        setState({ status: 'email', error: null, busy: false });
      }
    },

    async authorizedFetch(path, init) {
      const doFetch = (): Promise<Response> => {
        const headers = new Headers(init?.headers);
        headers.set('authorization', `Bearer ${accessToken ?? ''}`);
        return fetch(`${base}${path}`, { ...init, credentials: 'include', headers });
      };

      let response = await doFetch();
      if (response.status === 401) {
        if (!(await refresh().catch(() => false))) {
          setState({ status: 'email', error: null, busy: false });
          return response;
        }
        response = await doFetch();
      }
      return response;
    },
  };
}
